/**
 * Two-stage loading for the heavy third-party tags (Meta fbevents.js,
 * Microsoft Clarity). Each tag's queue STUB still mounts `afterInteractive`
 * inside its component, so every `fbq(...)` / `clarity(...)` call the page
 * makes is queued from the first hydration effect exactly as before — nothing
 * the page fires is lost. Only the external library, the part that costs
 * main-thread time, is injected here, on the FIRST of:
 *
 *  1. `interaction` — a real user input on window (pointerdown, touchstart,
 *     keydown, click, mousemove, wheel; capture phase, passive, detached after
 *     the first hit). `scroll` is deliberately excluded: scroll restoration,
 *     hash anchors and the PDP rails' `scrollTo` calls fire scroll events with
 *     nobody present, which would defeat the deferral in exactly the
 *     no-interaction runs it exists to keep clean.
 *  2. `fallback` — `fallbackMs` after the window `load` event (or after
 *     registration when the document is already complete). Anchored to load,
 *     NOT to requestIdleCallback: load + rIC still lands inside the Lighthouse
 *     trace window on a throttled CPU (Clarity's old `lazyOnload` measured
 *     336 ms of blocking time on the exam PDP).
 *  3. `ceiling` — HARD_CEILING_MS after registration, for a `load` event that a
 *     hung resource holds open.
 *  4. `demand` — a caller that needs the library NOW: checkout must carry the
 *     `_fbp` cookie into Stripe metadata, the success page fires the browser
 *     Purchase. Skips the stagger.
 *
 * A library is injected at most once per page: the promise is memoised by id
 * and survives component unmount. Consent is enforced by WHO registers — the
 * tag components sit inside ConsentGate, so a denied visitor registers
 * nothing, arms no listener and loads nothing. gtag.js and fpr.js are NOT
 * routed through here: GA4 session stitching (`_ga`/`_ga_<id>`) has no
 * server-side fallback and fpr.js is 2 KB.
 */

export type DeferredTagId = 'fbevents' | 'clarity'

export type DeferredTagTrigger =
  | 'interaction'
  | 'fallback'
  | 'ceiling'
  | 'demand'

export interface DeferredTagSpec {
  id: DeferredTagId
  src: string
  /** No-interaction ceiling, measured from the window `load` event. */
  fallbackMs: number
  /** Extra delay between a shared trigger and injection, so two libraries
   *  never evaluate back to back on the user's first tap. Ignored for
   *  `demand`. Default 0. */
  settleMs?: number
}

export const INTERACTION_EVENTS = [
  'pointerdown',
  'touchstart',
  'keydown',
  'click',
  'mousemove',
  'wheel',
] as const

export const HARD_CEILING_MS = 10_000

const LISTENER_OPTIONS: AddEventListenerOptions = {
  capture: true,
  passive: true,
}

interface Registration {
  fire: (trigger: DeferredTagTrigger) => void
}

const registrations = new Map<DeferredTagId, Registration>()
const injections = new Map<DeferredTagId, Promise<boolean>>()
/** Demands that arrived before the tag component registered. React flushes
 *  effects child-first, so a page effect (the success page's Purchase) can ask
 *  for a library before the layout's tag component has armed the loader. */
const demanded = new Set<DeferredTagId>()

function injectScript(
  spec: DeferredTagSpec,
  trigger: DeferredTagTrigger
): Promise<boolean> {
  const existing = injections.get(spec.id)
  if (existing) return existing
  const promise = new Promise<boolean>((resolve) => {
    const el = document.createElement('script')
    el.async = true
    el.src = spec.src
    el.dataset.deferredTag = spec.id
    el.dataset.deferredTrigger = trigger
    el.addEventListener('load', () => resolve(true), { once: true })
    el.addEventListener('error', () => resolve(false), { once: true })
    document.head.appendChild(el)
  })
  injections.set(spec.id, promise)
  return promise
}

/**
 * Arm the loader for one library. Returns the disarm function for the caller's
 * effect cleanup; disarming never removes a script that was already injected.
 */
export function registerDeferredTag(spec: DeferredTagSpec): () => void {
  if (typeof window === 'undefined' || injections.has(spec.id)) {
    return () => {}
  }

  let fallbackTimer: number | undefined
  let settleTimer: number | undefined
  let armed = true

  function disarm(): void {
    if (!armed) return
    armed = false
    for (const type of INTERACTION_EVENTS) {
      window.removeEventListener(type, onInteraction, LISTENER_OPTIONS)
    }
    window.removeEventListener('load', armFallback)
    window.clearTimeout(fallbackTimer)
    window.clearTimeout(ceilingTimer)
  }

  function inject(trigger: DeferredTagTrigger): void {
    window.clearTimeout(settleTimer)
    settleTimer = undefined
    registrations.delete(spec.id)
    void injectScript(spec, trigger)
  }

  function fire(trigger: DeferredTagTrigger): void {
    disarm()
    if (injections.has(spec.id)) {
      registrations.delete(spec.id)
      return
    }
    const settle = trigger === 'demand' ? 0 : (spec.settleMs ?? 0)
    if (settle === 0) {
      inject(trigger)
    } else if (settleTimer === undefined) {
      settleTimer = window.setTimeout(() => inject(trigger), settle)
    }
  }

  function onInteraction(): void {
    fire('interaction')
  }

  function armFallback(): void {
    fallbackTimer = window.setTimeout(() => fire('fallback'), spec.fallbackMs)
  }

  // The ceiling is armed before anything can call fire(), so disarm() never
  // reads it inside its temporal dead zone — a demand recorded before this
  // component mounted fires during registration, below.
  const ceilingTimer = window.setTimeout(() => fire('ceiling'), HARD_CEILING_MS)
  registrations.set(spec.id, { fire })

  if (demanded.has(spec.id)) {
    demanded.delete(spec.id)
    fire('demand')
    return () => {}
  }

  for (const type of INTERACTION_EVENTS) {
    window.addEventListener(type, onInteraction, LISTENER_OPTIONS)
  }
  if (document.readyState === 'complete') {
    armFallback()
  } else {
    window.addEventListener('load', armFallback, { once: true })
  }

  return () => {
    disarm()
    window.clearTimeout(settleTimer)
    registrations.delete(spec.id)
  }
}

/**
 * Inject a registered library right now (`demand`). Resolves true once it has
 * executed, false on error, and false immediately when nothing is registered —
 * consent denied, id unset, ad-blocked — so callers never wait for a tag that
 * is not coming. A demand that arrives before the tag component registered is
 * remembered and injected the moment it does.
 */
export function loadDeferredTag(id: DeferredTagId): Promise<boolean> {
  const registration = registrations.get(id)
  if (registration) {
    registration.fire('demand')
  } else if (!injections.has(id)) {
    // Nothing armed yet: remember the demand so registration injects at once.
    demanded.add(id)
  }
  return injections.get(id) ?? Promise.resolve(false)
}

const READY_POLL_MS = 50

/**
 * `loadDeferredTag` bounded by `timeoutMs`, optionally until `isReady()` holds.
 * The predicate exists because "script executed" is not "side effect done":
 * fbevents.js fetches its dataset config before it writes `_fbp`, so the
 * checkout boundary passes `_fbp`-is-present and polls for it. Zero latency
 * when the predicate already holds (a returning visitor's cookie), when the
 * library is already loaded, or when it was never registered / errored; the
 * wait only exists for a visitor whose very first interaction is the call
 * site itself, and a false result means "proceed without it".
 */
export function whenDeferredTagReady(
  id: DeferredTagId,
  timeoutMs: number,
  isReady?: () => boolean
): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false)
  if (isReady?.()) return Promise.resolve(true)
  const loading = loadDeferredTag(id)
  return new Promise<boolean>((resolve) => {
    let poll: number | undefined
    const timer = window.setTimeout(() => {
      window.clearInterval(poll)
      resolve(false)
    }, timeoutMs)
    function settle(ok: boolean): void {
      window.clearTimeout(timer)
      window.clearInterval(poll)
      resolve(ok)
    }
    void loading.then((loaded) => {
      if (!loaded || !isReady) {
        settle(loaded)
        return
      }
      if (isReady()) {
        settle(true)
        return
      }
      poll = window.setInterval(() => {
        if (isReady()) settle(true)
      }, READY_POLL_MS)
    })
  })
}
